import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { companyInfo } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function CarportPage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = [
    { src: "/_assets/pxl-20260529-082343854.jpg", title: "Stålcarport med koksgrå sidebeklædning og fliser" },
    { src: "/_assets/295322415-584214203079039-442631182103604215-n.jpg", title: "Carport med indgang" },
    { src: "/_assets/thumbnail-1000004142.jpg", title: "Carport affaldssortering" },
    { src: "/_assets/thumbnail-1000004145.jpg", title: "Carport med skur" },
    { src: "/_assets/thumbnail-img20250813095918.jpg", title: "Carport i komposit" },
    { src: "/_assets/thumbnail-img20250813100319.jpg", title: "Carport i Odense" },
    { src: "/_assets/thumbnail-img20250813100327_0b99b509.jpg", title: "Carport i Danmark" },
    { src: "/_assets/thumbnail-1000003547.jpg", title: "Stålcarport montering" },
    { src: "/_assets/thumbnail-1000006619.jpg", title: "Dobbelt carport" },
    { src: "/_assets/6059.jpg", title: "Carport med beklædning" },
  ];

  return (
    <>
      <SEO
        title="Carport i Danmark | Læs om 5 fordele | Kontakt her 🏠🚗"
        description="Ønsker du en carport med eller uden skur af høj kvalitet og lang holdbarhed? Hovendals er den ideelle partner. Kontakt os og få tilbud i dag."
        ogImage="/_assets/thumbnail-1000004137-0.jpg"
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/thumbnail-1000004137-0.jpg", "width=1600,quality=80,format=auto")}
            alt="Carport"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-car" aria-hidden="true"></i>
              <span>Carporte i stål & komposit</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Carport - beskyttelse og æstetik i ét
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              En carport er ikke bare en praktisk løsning til at beskytte din bil mod regn, sne og sol – det er også en investering i både funktionalitet og udseende på din ejendom. Hos Hovendals designer og bygger vi carporte, der matcher både husets stil og dine behov
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Skræddersyet design</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  Funktionelle og holdbare carporte
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                Hos <Link to="/" className="text-[#004fa4] font-bold hover:underline">Hovendals</Link> tilbyder vi carporte, der kombinerer funktionalitet, styrke og et flot design. Uanset om du ønsker en klassisk løsning, en carport med redskabsrum eller plads til flere biler, bygger vi i robuste materialer, der sikrer lang holdbarhed og minimal vedligeholdelse.
              </p>

              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">
                  Vi tilbyder:
                </h3>
                <ul className="space-y-2 text-sm text-slate-700 font-medium">
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Enkle og klassiske carporte til daglig brug</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Avancerede løsninger med integreret redskabsrum</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Mulighed for plads til flere biler</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Robuste materialer med lang holdbarhed</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Minimal vedligeholdelse og høj funktionalitet</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Skræddersyede løsninger i træ, stål eller kombination</span>
                  </li>
                </ul>
              </div>

              <p className="text-slate-700 leading-relaxed text-sm">
                Uanset om du ønsker en carport i træ, stål eller en kombination, skaber vi en løsning, der giver dig tryghed, funktionalitet og et smukt helhedsindtryk af dit hjem.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring os op</span>
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-envelope" aria-hidden="true"></i>
                  <span>Skriv en mail</span>
                </a>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-slate-100">
              <img
                src={imageUrl("/_assets/carport-skur.webp", "width=1200,quality=85,format=auto")}
                alt="Moderne stålcarport og skurkonstruktion"
                className="w-full h-auto aspect-[16/10] object-cover"
              />
            </div>
          </div>

          {/* Section: Fordele ved stålcarport */}
          <div className="bg-slate-50 rounded-2xl p-8 sm:p-12 border border-slate-200 mb-16 space-y-4">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Fordele ved en stålcarport
            </h3>
            <p className="text-slate-700 leading-relaxed text-base">
              Der er mange fordele ved at overveje en carport med skur i stål. Stål er kendt for sin styrke og lang levetid. En carport og skur i stål kan modstå hårde vejrforhold som kraftig vind, sne og regn, hvilket gør den til en ideel løsning for ethvert klima. Den rustmodstandige behandling sikrer, at din carport vil holde sig pæn og ikke kræver minimal vedligeholdelse i mange år frem.
            </p>
          </div>

          {/* Galleri */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Carport galleri
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Klik på et billede for at se forstørret visning.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-48 rounded-xl overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-md transition-all"
                >
                  <img
                    src={imageUrl(img.src, "width=600,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="p-2.5 rounded-full bg-[#143463]/90 text-white text-sm">
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </span>
                  </div>
                  {img.title && (
                    <div className="absolute bottom-2 left-2 right-2 bg-slate-950/70 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded truncate">
                      {img.title}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section: Skræddersyet carport & Rådgivning */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-xl font-bold text-slate-900">
                Skræddersyet carport med eller uden skur
              </h3>
              <p className="text-slate-700 leading-relaxed text-sm">
                Hos Hovendals forstår vi, at hver kundes behov er unikke, og derfor tilbyder vi fuldt skræddersyede carport løsninger, både med og uden skur. Uanset om du har brug for en enkelt carport til én bil eller en dobbelt carport med skur, kan en stålkonstruktion tilpasses fuldstændigt.
              </p>
              <p className="text-slate-700 leading-relaxed text-sm">
                Stålcarports kommer i en bred vifte af stilarter og finish, hvilket gør det muligt for dig at matche din nye carport med dit hjems udseende og din personlige stil. Fra minimalistisk moderne design til traditionel stil, uanset din smag, kan du designe en carport, der både er praktisk og æstetisk tiltalende.
              </p>
              <Link to="/kontakt" className="inline-block text-sm font-bold text-[#004fa4] hover:underline">
                Få en solid carport i dag &rarr;
              </Link>
            </div>

            <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-xl font-bold text-slate-900">
                Personlig rådgivning, der sikrer dig det rette valg af carport
              </h3>
              <p className="text-slate-700 leading-relaxed text-sm">
                Hos Hovendals er personlig rådgivning nøgleordet, når det handler om at vælge den perfekte stål carport. Vi står klar til at vejlede dig gennem hele processen fra de indledende overvejelser omkring materialevalg og design. Måske tænker du, hvad passer bedst til min ejendom eller måske mangler du inspiration i forhold til hvad der kan lade sig gøre?
              </p>
              <p className="text-slate-700 text-xs font-bold uppercase tracking-wider text-slate-500">Vi kan blandt andet tilbyde:</p>
              <ul className="space-y-1.5 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                  <span>Dobbelt carport med skur eller uden skur</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                  <span>Stålcarport med redskabsrum</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                  <span>Væghængt carport</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                  <span>Carport med cykelskur</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                  <span>Fritstående carport</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed text-xs pt-2">
                Vi forstår vigtigheden af at tage hensyn til både funktionelle krav og æstetiske ønsker, så din carport harmonerer med din boligs stil og dit personlige udtryk. Vores mål er at sikre, at din investering ikke blot er praktisk, men også øger værdien for din ejendom. Med Hovendals ved din side får du ikke kun en stålcarport, men en gennemtænkt og holdbar løsning skræddersyet til din livsstil.
              </p>
            </div>
          </div>

          {/* Kontakt */}
          <div className="bg-slate-50 rounded-2xl p-8 sm:p-10 border border-slate-200 mb-16 space-y-4">
            <h3 className="text-2xl font-bold text-slate-900">
              Kontakt os i dag for et uforpligtende tilbud
            </h3>
            <p className="text-slate-700 leading-relaxed text-base">
              En stål carport med eller uden skur er en fremragende investering for enhver ejendom og ikke mindst din bil. Ring allerede i dag og få en god snak om, hvordan vi kan hjælpe dig i mål med din nye carport. Vi tilbyder også enkelstående <Link to="/skur" className="text-[#004fa4] font-bold hover:underline">skur</Link>, hvis det har interesse.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              Fang os på telefon <a href={`tel:${companyInfo.phoneFormatted}`} className="text-[#004fa4] font-bold hover:underline">{companyInfo.phone}</a> eller send en mail til <a href={`mailto:${companyInfo.email}`} className="text-[#004fa4] font-bold hover:underline">{companyInfo.email}</a> Du er også velkommen til at udfylde vores kontaktformular, så hører du fra os snarest.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-6 py-2.5 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
              >
                Ring os op
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="px-6 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all"
              >
                Skriv en mail
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox modal */}
      <LightboxModal
        images={images}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(index) => setLightboxIndex(index)}
      />
    </>
  );
}
